import {ChangeDetectorRef, Component} from '@angular/core';

import {finalize, tap} from 'rxjs/operators';

import * as moment from 'moment';

import {DATE_MIN} from '../../../app.constants';
import {Destroyable} from '../../../destroyable.component';
import {Enums} from '../../../shared/utils/enums';
import {PersonDataDTO, PersonDTO, PersonModelMetadataDTO, SdcLogDTO, SdcLogStatuses,} from '../sdconnect.model';
import {SdconnectService} from '../sdconnect.service';

@Component({
  selector: 'gp-sdc-person-form',
  templateUrl: './sdc-person-form.component.html',
  styleUrls: ['./sdc-person-form.component.scss']
})
export class SdcPersonFormComponent extends Destroyable {

  private readonly START_DATE_FIELD = 'startDate';
  public readonly statuses: string[] = Enums.getNames(SdcLogStatuses);
  public readonly personEntities = ['Address', 'BankAccount', 'PersonHistory'];
  public readonly employmentEntities = ['Contract', 'Salary', 'Organization', 'Ledger', 'WorkSchedule', 'Facility', 'CompanyCar'];

  public person: PersonDTO;

  public loading = false;
  public errorPanelOpened = false;
  public expandAll = false;

  constructor(private sdconnectService: SdconnectService,
              private cd: ChangeDetectorRef) {
    super();
  }

  public metadata(entity: string): PersonModelMetadataDTO {
    return this.person.metadata.find(metadata => metadata.name === entity);
  }

  public data(entity: string): PersonDataDTO[] {
    return this.person?.data[entity]?.sort((a, b) => {
      return -this.startDate(a).diff(this.startDate(b));
    });
  }

  public familyMemberStandardFields(): string[] {
    return SdconnectService.standardFields(this.familyMemberMetadata());
  }

  public familyMemberIsDated(): boolean {
    return SdconnectService.entityIsDated(this.familyMemberMetadata());
  }

  public familyMemberAdditionalFields(): string[] {
    return SdconnectService.additionalFields(this.familyMemberMetadata(), this.data('FamilyMember'));
  }

  public familyMemberHistoryData(familyMember: PersonDataDTO) {
    return this.data('FamilyMemberHistory')
      .filter(familyMemberHistory => familyMemberHistory.parentKey === familyMember.key);
  }

  public load(contractId: string) {
    this.loading = true;
    this.expandAll = false;
    this.sdconnectService.getPerson(contractId)
      .pipe(
        tap((person) => this.person = person),
        finalize(() => {
          this.loading = false;
          this.cd.detectChanges();
        }),
      )
      .subscribe();
  }

  public hasErrors(): boolean {
    return this.failedSdcLogs().length > 0;
  }

  public failedSdcLogs(): SdcLogDTO[] {
    return this.person?.sdcLogs?.filter(sdcLog => sdcLog.status === SdcLogStatuses.FAILED) || [];
  }

  public failedEntities(): string[] {
    return Object.entries(this.person?.data || {})
      .filter(([, items]) =>
        (items || []).some(item => item.status === SdcLogStatuses.FAILED))
      .map(([entity]) => entity);
  }

  private familyMemberMetadata() {
    return this.metadata('FamilyMember');
  }

  public openErrorPanel(): void {
    this.errorPanelOpened = true;
  }

  public onChange(sidePanelOpenChange: boolean): void {
    this.errorPanelOpened = sidePanelOpenChange;
  }

  public toggleExpandAll(): void {
    this.expandAll = !this.expandAll;
  }

  public scrollTo(entity: string): void {
    if (!entity) {
      return;
    }
    const el = document.getElementById(`${entity}-entityBlock`);
    el?.scrollIntoView({behavior: 'smooth', block: 'start'});
  }

  private startDate(item: PersonDataDTO) {
    return moment(item?.values?.[this.START_DATE_FIELD] || DATE_MIN);
  }
}
